import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FoldersBrowser } from '@/modules/folders/FoldersBrowser';
import { CreateMenu } from '@/modules/folders/components/CreateMenu';
import { MoveDialog } from '@/modules/folders/components/MoveDialog';
import { NodeCard } from '@/modules/folders/components/NodeCard';
import { NodeList } from '@/modules/folders/components/NodeList';
import type { NodeAction } from '@/modules/folders/components/NodeMenu';
import { RenameDialog } from '@/modules/folders/components/RenameDialog';
import { useSession } from '@/shared/auth';
import { useBrand } from '@/shared/brand/useBrand';
import { Icon } from '@/shared/components/ui/Icon';
import { formatRelativeTime } from '@/shared/i18n';
import { useI18n } from '@/shared/i18n/useT';
import { useOrgDirectory } from '@/shared/people/useOrgDirectory';
import {
  createFoldersShareAdapter,
  editorPathFor,
  folderPath,
  foldersApi,
  FOLDERS_RESOURCE_TYPE,
  useFoldersErrors,
  useNodeCreate,
} from '@/shared/folders';
import type { FoldersNode } from '@/shared/folders';
import { useOrg } from '@/shared/org';
import { ShareDialog } from '@/shared/sharing';
import { HomeCardGrid, HomeSection } from './home/HomeSection';
import { HomeSearch } from './home/HomeSearch';
import { HomeSearchResults } from './home/HomeSearchResults';
import { LibraryTab } from './library/LibraryTab';
import { useLibraryRecents } from './library/libraryRecents';
import { RecentSection } from './home/RecentSection';
import { useHomeWorkspace } from './home/useHomeWorkspace';
import { HOME_TAB_ICONS, HOME_TABS, useHomeTab } from './home/useHomeTab';
import type { HomeTab } from './home/useHomeTab';
import { useHomeView, ViewSwitch } from './home/ViewSwitch';

/** Tabs the phone bottom bar shows directly; the rest sit behind More. */
const MORE_TABS: readonly HomeTab[] = ['library', 'trash'];
const PRIMARY_TABS = HOME_TABS.filter((id) => !MORE_TABS.includes(id));

/**
 * numtable.com/ — the workspace.
 *
 * This used to be the module grid, which told a returning user nothing about
 * their own work; that grid now lives at `/apps`. What is here instead is the
 * shape every file manager settled on: what you marked, what someone just gave
 * you, what you had open, and then your folders.
 *
 * Every data section hides itself when empty, so a new account sees the search
 * field and an empty folder rather than five headings over
 * nothing.
 *
 * It imports from `@/modules/folders` on purpose. `src/modules/AGENTS.md` rule 1
 * forbids a *module* importing another module; composing modules is what the
 * shell is for, and `AppShell` already mounts all thirteen.
 */
export function Home() {
  const { t, formatLocale } = useI18n();
  const brand = useBrand();
  const org = useOrg();
  const session = useSession();

  const orgId = org.status === 'ready' ? org.orgId : null;
  const userId = session.session?.user.id ?? null;
  const readOnly = org.status === 'ready' && org.isViewingAs;

  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [foldersKey, setFoldersKey] = useState(0);
  const [renaming, setRenaming] = useState<FoldersNode | null>(null);
  const [moving, setMoving] = useState<FoldersNode | null>(null);
  const [sharing, setSharing] = useState<FoldersNode | null>(null);
  const [busy, setBusy] = useState(false);
  const errors = useFoldersErrors();
  const { tab, folderId, setTab } = useHomeTab();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreMenuRef = useRef<HTMLElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  // The More drawer closes on a press outside it or on Escape.
  useEffect(() => {
    if (!moreOpen) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (moreMenuRef.current?.contains(target) || moreButtonRef.current?.contains(target)) return;
      setMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);
  const [view, setView] = useHomeView(tab);

  const workspace = useHomeWorkspace(orgId, userId);
  const libraryRecents = useLibraryRecents();
  const ownerNames = useOrgDirectory(orgId);
  // A share stops being new once it has been opened since it was shared.
  const newSharedIds = useMemo(
    () =>
      new Set(
        workspace.shared
          .filter(({ sharedAt, openedAt }) => !openedAt || Date.parse(openedAt) < Date.parse(sharedAt))
          .map(({ node }) => node.id),
      ),
    [workspace.shared],
  );

  const create = useNodeCreate({
    orgId,
    parentId: folderId,
    userId,
    readOnly,
    // A file created from Home lands in the folder open in My files (the root
    // elsewhere), which the inline browser shows — so it has to refetch.
    onCreated: useCallback(() => setFoldersKey((n) => n + 1), []),
  });

  useEffect(() => {
    document.title = brand.name;
  }, [brand.name]);

  const copyLink = useCallback(
    async (node: FoldersNode) => {
      const url = new URL(
        node.kind === 'folder' ? folderPath(node.id) : editorPathFor(node.id, node.docType),
        window.location.origin,
      ).toString();
      try {
        await navigator.clipboard.writeText(url);
        setNotice(t('folders.linkCopied'));
      } catch {
        // Clipboard access can be refused; showing the URL beats claiming success.
        setNotice(url);
      }
    },
    [t],
  );

  // Memoized because `ShareDialog` reloads whenever the adapter identity changes.
  const shareAdapter = useMemo(
    () =>
      org.status === 'ready' && userId
        ? createFoldersShareAdapter({ userId, role: org.role })
        : null,
    [org, userId],
  );

  /** Same shape as the browser's writes: one at a time, errors in words, then
   *  refresh both the sections and the My files browser. */
  const run = useCallback(
    async (action: () => Promise<void>, fallbackKey: Parameters<typeof errors.report>[1]) => {
      if (busy || readOnly) return;
      setBusy(true);
      errors.clear();
      setNotice(null);
      try {
        await action();
        workspace.reload();
        setFoldersKey((n) => n + 1);
      } catch (cause) {
        errors.report(cause, fallbackKey);
      } finally {
        setBusy(false);
      }
    },
    [busy, errors, readOnly, workspace],
  );

  /** Star and Copy link for everything; the browser's edit actions only on
   *  what you own. */
  const actionsFor = useCallback(
    (node: FoldersNode): NodeAction[] => {
      const actions: NodeAction[] = [
        { id: 'link', labelKey: 'folders.copyLink', icon: 'link', run: () => void copyLink(node) },
      ];
      if (!readOnly && userId) {
        actions.unshift({
          id: 'star',
          labelKey: workspace.starredIds.has(node.id) ? 'folders.unstar' : 'folders.star',
          icon: 'star',
          run: () => workspace.toggleStar(node),
        });
      }
      // Rename, move, share and trash only on what you own: on someone else's
      // file they would fail under RLS anyway, and a menu of errors is worse.
      if (!readOnly && userId && node.createdBy === userId) {
        actions.splice(
          1,
          0,
          { id: 'rename', labelKey: 'folders.rename', icon: 'pencil', run: () => setRenaming(node) },
          { id: 'move', labelKey: 'folders.move', icon: 'folderMove', run: () => setMoving(node) },
          { id: 'share', labelKey: 'folders.share', icon: 'user', run: () => setSharing(node) },
        );
        actions.push({
          id: 'trash',
          labelKey: 'folders.sendToTrash',
          icon: 'trash',
          danger: true,
          run: () => void run(() => foldersApi.trashNode(node.id), 'folders.error.trash'),
        });
      }
      return actions;
    },
    [copyLink, readOnly, run, userId, workspace],
  );

  const searching = submitted.trim().length >= 2;

  return (
    <div className="flex min-w-0 flex-col gap-6 pb-24 sm:pb-8">
      <div className="flex flex-wrap items-center gap-3 pt-1">
        <div className="hidden sm:block">
          <CreateMenu
            onCreateFolder={create.createFolder}
            onCreateDocument={create.createDocument}
            disabled={create.disabled}
            align="left"
          />
        </div>
        {/* Phones get Create as a floating button above the bottom tab bar. */}
        <div
          className="fixed right-4 z-30 sm:hidden"
          style={{ bottom: 'calc(4.5rem + env(safe-area-inset-bottom))' }}
        >
          <CreateMenu
            onCreateFolder={create.createFolder}
            onCreateDocument={create.createDocument}
            disabled={create.disabled}
            fab
          />
        </div>
        <HomeSearch value={query} onValueChange={setQuery} onSubmit={setSubmitted} />

        <div className="ml-auto flex items-center gap-2">
          <ViewSwitch value={view} onChange={setView} />
          <Link
            to="/apps"
            aria-label={t('home.allApps')}
            title={t('home.allApps')}
            className="tap-target hidden sm:flex items-center justify-center rounded-token px-2 text-muted hover:bg-surface-3 hover:text-fg"
          >
            <Icon name="apps" filled className="h-5 w-5" />
          </Link>
        </div>
      </div>

      {create.errorMessage && (
        <p role="alert" className="rounded-token border border-danger p-3 text-sm text-danger">
          {create.errorMessage}
        </p>
      )}
      {errors.message && (
        <p role="alert" className="rounded-token border border-danger p-3 text-sm text-danger">
          {errors.message}
        </p>
      )}
      {notice && <p className="text-sm text-muted">{notice}</p>}

      {searching ? (
        <HomeSearchResults
          term={submitted.trim()}
          orgId={orgId}
          starredIds={workspace.starredIds}
          onToggleStar={workspace.toggleStar}
          actionsFor={actionsFor}
        />
      ) : (
        <>
          {workspace.failed && (
            <p role="alert" className="rounded-token border border-danger p-3 text-sm text-danger">
              {t('home.error.sections')}
            </p>
          )}

          <div
            role="tablist"
            aria-label={t('home.tabs.label')}
            className="nt-hidden-scrollbar hidden gap-1 sm:flex overflow-x-auto overflow-y-hidden shadow-[inset_0_-1px_0_var(--border)]"
          >
            {HOME_TABS.map((id) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={[
                  'tap-target flex shrink-0 items-center gap-2 border-b-2 px-3 text-sm',
                  tab === id ? 'border-accent font-medium text-fg' : 'border-transparent text-muted hover:text-fg',
                ].join(' ')}
              >
                <Icon name={HOME_TAB_ICONS[id]} className="h-4 w-4" />
                {t(`home.tabs.${id}`)}
                {id === 'shared' && newSharedIds.size > 0 && (
                  <span
                    aria-label={t('home.newCount', { count: newSharedIds.size })}
                    className="min-w-5 rounded-full bg-accent px-1.5 text-center text-xs font-semibold leading-5 text-accent-fg"
                  >
                    {newSharedIds.size}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Phones get the tabs as a bottom bar instead: four labelled targets
              plus More, which opens Numtable and Trash in a drawer above it. */}
          {moreOpen && (
            <nav
              ref={moreMenuRef}
              aria-label={t('home.tabs.more')}
              className="fixed right-3 z-40 flex flex-col overflow-hidden rounded-token border border-border bg-surface-2 p-1.5 shadow-lg sm:hidden"
              style={{ bottom: 'calc(4.75rem + env(safe-area-inset-bottom))' }}
            >
              {MORE_TABS.map((id) => (
                <button
                  key={id}
                  type="button"
                  aria-current={tab === id ? 'page' : undefined}
                  onClick={() => {
                    setTab(id);
                    setMoreOpen(false);
                  }}
                  className={[
                    'tap-target flex items-center gap-3 rounded-token px-3 text-sm',
                    tab === id ? 'bg-accent/10 font-medium text-accent' : 'text-fg',
                  ].join(' ')}
                >
                  <Icon name={HOME_TAB_ICONS[id]} className="h-5 w-5" />
                  {t(`home.tabs.${id}`)}
                </button>
              ))}
            </nav>
          )}
          <nav
            aria-label={t('home.tabs.label')}
            className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface-2 sm:hidden"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <ul className="grid grid-cols-5">
              {PRIMARY_TABS.map((id) => (
                <li key={id}>
                  <button
                    type="button"
                    aria-current={tab === id ? 'page' : undefined}
                    onClick={() => {
                      setTab(id);
                      setMoreOpen(false);
                    }}
                    className={[
                      'flex w-full flex-col items-center gap-0.5 px-1 pb-1.5 pt-2',
                      tab === id ? 'text-accent' : 'text-muted',
                    ].join(' ')}
                  >
                    <span className="relative">
                      <Icon name={HOME_TAB_ICONS[id]} className="h-6 w-6" />
                      {id === 'shared' && newSharedIds.size > 0 && (
                        <span
                          aria-label={t('home.newCount', { count: newSharedIds.size })}
                          className="absolute -right-2 -top-1 min-w-4 rounded-full bg-accent px-1 text-center text-[0.625rem] font-semibold leading-4 text-accent-fg"
                        >
                          {newSharedIds.size}
                        </span>
                      )}
                    </span>
                    <span className="w-full truncate text-center text-[0.6875rem] leading-tight">
                      {t(`home.tabs.${id}`)}
                    </span>
                  </button>
                </li>
              ))}
              <li>
                <button
                  ref={moreButtonRef}
                  type="button"
                  aria-expanded={moreOpen}
                  aria-label={t(moreOpen ? 'home.tabs.closeMore' : 'home.tabs.openMore')}
                  onClick={() => setMoreOpen((open) => !open)}
                  className={[
                    'flex w-full flex-col items-center gap-0.5 px-1 pb-1.5 pt-2',
                    moreOpen || MORE_TABS.includes(tab) ? 'text-accent' : 'text-muted',
                  ].join(' ')}
                >
                  <Icon name="moreMenu" className="h-6 w-6" />
                  <span className="w-full truncate text-center text-[0.6875rem] leading-tight">
                    {t('home.tabs.more')}
                  </span>
                </button>
              </li>
            </ul>
          </nav>

          <div role="tabpanel" className="flex min-w-0 flex-col gap-6">
            {tab === 'starred' &&
              (workspace.starred.length === 0 ? (
                <EmptyTab text={t('home.empty.starred')} />
              ) : view === 'list' ? (
                <NodeList
                  dateLabel={t('home.columns.edited')}
                  rows={workspace.starred.map((node) => ({
                    node,
                    actions: actionsFor(node),
                    date: formatRelativeTime(node.updatedAt, formatLocale),
                    starred: true,
                    ...(readOnly || !userId ? {} : { onToggleStar: () => workspace.toggleStar(node) }),
                  }))}
                />
              ) : (
                <HomeCardGrid>
                  {workspace.starred.map((node) => (
                    <li key={node.id}>
                      <NodeCard
                        node={node}
                        actions={actionsFor(node)}
                        starred
                        {...(readOnly || !userId ? {} : { onToggleStar: () => workspace.toggleStar(node) })}
                      />
                    </li>
                  ))}
                </HomeCardGrid>
              ))}

            {tab === 'recent' && (
              <>
                {workspace.recent.length === 0 && libraryRecents.length === 0 && <EmptyTab text={t('home.empty.recent')} />}
                <RecentSection
                  items={workspace.recent}
                  libraryItems={libraryRecents}
                  ownerNames={ownerNames}
                  currentUserId={userId}
                  actionsFor={actionsFor}
                  view={view}
                  starredIds={workspace.starredIds}
                  {...(readOnly || !userId ? {} : { onToggleStar: workspace.toggleStar })}
                />
                {workspace.frequent.length > 0 && (
                  <HomeSection title={t('home.frequentlyOpened')}>
                    <HomeCardGrid>
                      {workspace.frequent.map(({ node }) => (
                        <li key={node.id}>
                          <NodeCard
                            node={node}
                            actions={actionsFor(node)}
                            starred={workspace.starredIds.has(node.id)}
                            {...(readOnly || !userId ? {} : { onToggleStar: () => workspace.toggleStar(node) })}
                          />
                        </li>
                      ))}
                    </HomeCardGrid>
                  </HomeSection>
                )}
              </>
            )}

            {/* The whole Folders browser, at the root of the tree. Opening a
                folder stays here: the address becomes /folders/<id>
                and the breadcrumb shows the path. */}
            {tab === 'files' && <FoldersBrowser key={`${foldersKey}:${folderId ?? ''}`} embedded view={view} folderId={folderId} />}

            {tab === 'shared' &&
              (workspace.shared.length === 0 ? (
                <EmptyTab text={t('home.empty.shared')} />
              ) : view === 'list' ? (
                <NodeList
                  dateLabel={t('home.columns.shared')}
                  rows={workspace.shared.map(({ node, sharedAt }) => ({
                    node,
                    actions: actionsFor(node),
                    owner: (node.createdBy ? ownerNames.get(node.createdBy) : null) ?? t('home.owner.unknown'),
                    date: formatRelativeTime(sharedAt, formatLocale),
                    starred: workspace.starredIds.has(node.id),
                    isNew: newSharedIds.has(node.id),
                    ...(readOnly || !userId ? {} : { onToggleStar: () => workspace.toggleStar(node) }),
                  }))}
                />
              ) : (
                <HomeCardGrid>
                  {workspace.shared.map(({ node, sharedAt }) => {
                    const ownerName = node.createdBy ? ownerNames.get(node.createdBy) ?? null : null;
                    const date = formatRelativeTime(sharedAt, formatLocale);
                    return (
                      <li key={node.id}>
                        <NodeCard
                          node={node}
                          actions={actionsFor(node)}
                          unread={newSharedIds.has(node.id)}
                          ownerName={ownerName}
                          meta={
                            ownerName
                              ? t('home.sharedBy', { name: ownerName, date })
                              : t('home.sharedByUnknown', { date })
                          }
                        />
                      </li>
                    );
                  })}
                </HomeCardGrid>
              ))}

            {tab === 'library' && <LibraryTab view={view} />}

            {tab === 'trash' && <FoldersBrowser variant="trash" embedded view={view} />}
          </div>
        </>
      )}

      {renaming && (
        <RenameDialog
          initialName={renaming.name}
          onClose={() => setRenaming(null)}
          onSubmit={(name) => {
            const target = renaming;
            setRenaming(null);
            void run(() => foldersApi.renameNode(target.id, name), 'folders.error.rename');
          }}
        />
      )}

      {moving && orgId && (
        <MoveDialog
          node={moving}
          orgId={orgId}
          onClose={() => setMoving(null)}
          onSubmit={(destination) => {
            const target = moving;
            setMoving(null);
            void run(() => foldersApi.moveNode(target.id, destination), 'folders.error.move');
          }}
        />
      )}

      {sharing && shareAdapter && (
        <ShareDialog
          subject={{
            kind: 'resource',
            orgId: sharing.orgId,
            resourceType: FOLDERS_RESOURCE_TYPE,
            resourceId: sharing.id,
            name: sharing.name,
          }}
          adapter={shareAdapter}
          onClose={() => setSharing(null)}
        />
      )}
    </div>
  );
}

function EmptyTab({ text }: { text: string }) {
  return (
    <p className="rounded-token border border-dashed border-border p-8 text-center text-sm text-muted">{text}</p>
  );
}
